import ImageTracer from "imagetracerjs";

/**
 * Gemini's image model doesn't emit a real alpha channel — asking for a
 * "transparent" background just gets a plain flat backdrop painted in, so
 * the PNG that comes back is fully opaque even when it visually looks
 * transparent in a checkerboard preview.
 *
 * This recovers real transparency with a soft edge, not a binary cutout.
 * A hard "this pixel is background, make it fully transparent, this one
 * isn't, leave it fully opaque" cutoff throws away the anti-aliasing the
 * original image had at every curve and diagonal, which is what turns a
 * smooth edge into a visibly jagged, pixelated one once it's sitting on a
 * contrasting background.
 *
 * Instead, each pixel gets a graduated alpha based on how far its colour
 * sits from the sampled background: close to the background colour → near
 * transparent, clearly different → fully opaque, and a feathered ramp in
 * between that reproduces the original antialiasing. Pixels in that ramp
 * also get the background colour unmixed back out of them (`unmix`
 * below), which is what stops a pale halo/fringe from showing up around
 * the cutout — a known artifact of naive alpha-only background removal.
 */
export function toTransparentPng(
  dataUrl,
  { tolerance = 24, feather = 40 } = {},
) {
  return loadImage(dataUrl).then((img) => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const { data } = imageData;

    // Sample the backdrop colour from the top-left corner.
    const bg = [data[0], data[1], data[2]];
    const highThreshold = tolerance + feather;

    const distance = (i) => {
      const dr = data[i] - bg[0];
      const dg = data[i + 1] - bg[1];
      const db = data[i + 2] - bg[2];
      return Math.sqrt(dr * dr + dg * dg + db * db);
    };

    // Flood fill from the border, following any pixel still within the
    // feathered band — this is what lets the mask trace a soft,
    // antialiased letter edge instead of stopping dead at a hard pixel
    // boundary, while still stopping before it reaches solid foreground
    // (so it can't eat into the mark itself).
    const visited = new Uint8Array(width * height);
    const stack = [];
    const tryPush = (x, y) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return;
      const p = y * width + x;
      if (visited[p] || distance(p * 4) > highThreshold) return;
      visited[p] = 1;
      stack.push(p);
    };

    for (let x = 0; x < width; x++) {
      tryPush(x, 0);
      tryPush(x, height - 1);
    }
    for (let y = 0; y < height; y++) {
      tryPush(0, y);
      tryPush(width - 1, y);
    }

    while (stack.length) {
      const p = stack.pop();
      const x = p % width;
      const y = (p / width) | 0;
      tryPush(x + 1, y);
      tryPush(x - 1, y);
      tryPush(x, y + 1);
      tryPush(x, y - 1);
    }

    // Below this, skip unmixing rather than divide by it — dividing by an
    // alpha this close to zero amplifies ordinary rounding noise into
    // wildly wrong colours (a stray bright or saturated pixel). At this
    // little opacity the pixel is nearly invisible anyway, but that wrong
    // colour was still faintly visible: exactly the ghost/halo edge
    // sitting just outside each letter in the screenshot.
    const MIN_UNMIX_ALPHA = 0.12;

    for (let p = 0; p < visited.length; p++) {
      const i = p * 4;
      const d = distance(i);
      let alpha = (d - tolerance) / feather;
      alpha = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;

      if (alpha > 0 && alpha < 1) {
        // Unmix: this pixel's colour is (alpha * trueColour) + ((1-alpha)
        // * background); solving for trueColour removes the background's
        // contribution instead of just leaving it partially visible. The
        // divisor is floored, not the stored alpha — transparency still
        // ramps smoothly, only the color-correction math is protected.
        const divisor = Math.max(alpha, MIN_UNMIX_ALPHA);
        data[i] = clamp255((data[i] - (1 - alpha) * bg[0]) / divisor);
        data[i + 1] = clamp255((data[i + 1] - (1 - alpha) * bg[1]) / divisor);
        data[i + 2] = clamp255((data[i + 2] - (1 - alpha) * bg[2]) / divisor);
      }
      data[i + 3] = Math.round(alpha * 255);
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL("image/png");
  });
}

function clamp255(value) {
  return value < 0 ? 0 : value > 255 ? 255 : value;
}

/**
 * Vectorises a PNG into real SVG paths (not just a raster image wrapped in
 * an <svg> tag). Best run on the output of `toTransparentPng` first, so
 * the backdrop traces as transparent instead of as a big background path.
 */
export function toSvg(dataUrl) {
  return new Promise((resolve, reject) => {
    try {
      ImageTracer.imageToSVG(dataUrl, resolve, {
        scale: 1,
        numberofcolors: 24,
        // A light blur pass before tracing smooths out pixel-level noise
        // at the edges, so the traced path follows the curve rather than
        // faceting around individual pixels.
        blurradius: 2,
        blurdelta: 20,
        roundcoords: 1,
      });
    } catch (err) {
      reject(err);
    }
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error("Couldn't load that image to process it."));
    img.src = src;
  });
}

/** Downloads a data: or blob: URL under the given filename. */
export function downloadUrl(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Downloads SVG text as a .svg file. */
export function downloadSvgText(svgText, filename) {
  const blob = new Blob([svgText], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  downloadUrl(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** "image/jpeg" → "jpg", "image/png" → "png", etc. */
export function extensionForMime(mimeType) {
  if (!mimeType) return "png";
  const subtype = (mimeType.split("/")[1] || "png").split("+")[0];
  return subtype === "jpeg" ? "jpg" : subtype;
}

/** Brand name → a safe file name stem. */
export function slugForFilename(text, fallback = "logo") {
  const slug = (text || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallback;
}
